import React from "react";
import { Box, Typography, TextField, IconButton, Stack } from "@mui/material";
import { DeleteOutlineOutlined, Add } from "@mui/icons-material";

/**
 * Controlled editor for a single system -> device list entry.
 * `name`, `devices` and `deviceInput` are all owned by the parent so the parent's
 * dialog "Update"/"Add" action (rendered outside this component) can read the
 * current values. `deviceInput` is parent-owned so that a device typed into the
 * "Device name" field but not yet committed with "+" can still be included when
 * the parent saves. Reused by both the "Add System" and "Edit System" flows in
 * the Device List tab.
 */
export default function SystemDeviceEditor({
  name,
  onNameChange,
  devices,
  onAddDevice,
  onDeleteDevice,
  deviceInput,
  onDeviceInputChange,
  nameError = "",
}) {
  const isDuplicateDevice = (value) =>
    devices.some((d) => typeof d === "string" && d.toLowerCase() === value.trim().toLowerCase());

  const handleAddDevice = () => {
    const value = deviceInput.trim();
    if (!value || isDuplicateDevice(value)) return;
    onAddDevice(value);
    onDeviceInputChange("");
  };

  const handleDeviceInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddDevice();
    }
  };

  const trimmedInput = deviceInput.trim();
  const isDuplicate = trimmedInput !== "" && isDuplicateDevice(trimmedInput);
  const canAddDevice = trimmedInput !== "" && !isDuplicate;

  return (
    <Box sx={{ py: 1 }}>
      <TextField
        label="System Name"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        error={!!nameError}
        helperText={nameError}
        size="small"
        fullWidth
        autoFocus
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Devices
      </Typography>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
        <TextField
          label="Device name"
          value={deviceInput}
          onChange={(e) => onDeviceInputChange(e.target.value)}
          onKeyDown={handleDeviceInputKeyDown}
          error={isDuplicate}
          helperText={isDuplicate ? "This device is already in the list" : ""}
          size="small"
          fullWidth
          placeholder="e.g. emulator-5554"
        />
        <IconButton
          onClick={handleAddDevice}
          disabled={!canAddDevice}
          sx={{ mt: 0.25 }}
          aria-label="Add device"
        >
          <Add />
        </IconButton>
      </Box>

      <Stack spacing={1} sx={{ maxHeight: "260px", overflowY: "auto", pr: 0.5 }}>
        {devices.length > 0 ? (
          devices.map((device, index) => (
            <Box
              key={`${device}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 0.75,
                border: "1px solid #e0e0e0",
                borderRadius: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.875rem" }}>
                {device}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onDeleteDevice(index)}
                sx={{ color: "#d32f2f" }}
                aria-label={`Delete ${device}`}
              >
                <DeleteOutlineOutlined fontSize="small" />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No devices added yet
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
